'use client'

/**
 * §19.10.1 — "the return state: work without its audio." Rendered in place
 * of the empty dropzone when sessionStorage has a waiting return-state
 * record but no audio has been re-attached yet this page-load.
 */

import { cn } from '@/lib/utils'
import { fmtDurationPrecise } from '@/lib/tls-format'
import type { CreateReturnState } from './create-session-persistence'


interface ReturnStateBannerProps {
  state: CreateReturnState
  className?: string
}

export function ReturnStateBanner({ state, className }: ReturnStateBannerProps) {
  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <div className="border border-dashed border-[color:var(--tls-border-strong)] px-5 py-6 text-center">
        <p className="text-base font-semibold text-[var(--tls-fg-strong)]">
          Your edits are here. The audio is not.
        </p>
        <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-[var(--tls-fg-muted)]">
          A refresh cannot keep an audio file in the browser. Drop{' '}
          <span className="font-medium text-[var(--tls-fg-strong)]">{state.fileName}</span> again and everything
          below re-applies itself.
        </p>
      </div>
      <div>
        <p className="mb-2 tls-eyebrow">
          Waiting to re-apply
        </p>
        <dl className="flex flex-col text-sm">
          {[
            ['Trim', `${fmtDurationPrecise(state.regionStart)} → ${fmtDurationPrecise(state.regionEnd)}`],
            ['Fade in', fmtDurationPrecise(state.fadeIn)],
            ['Fade out', fmtDurationPrecise(state.fadeOut)],
            ['Gain', state.fineTuneGainDb === 0 ? 'none' : `${state.fineTuneGainDb > 0 ? '+' : ''}${state.fineTuneGainDb.toFixed(1)} dB`],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-[color:var(--tls-border-subtle)] py-1.5">
              <dt className="text-[var(--tls-fg-muted)]">{k}</dt>
              <dd className="tlsr-mono font-medium text-[var(--tls-fg-strong)]">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}
